/**
 * WidgetInfoButton — click-only info popover for analytics widget titles
 * (CORE-1133). Renders a small (i) button at the end of a widget's h3;
 * click toggles a portalled popover with a short description of what the
 * widget measures. Closes on outside-click or Escape.
 *
 * Distinct from FloatingTooltip (hover-based) — this is click-only so
 * touch users can read the description without hovering, and so the
 * description stays open while the reader parses it.
 */

import { createPortal } from 'react-dom';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface WidgetInfoButtonProps {
  /**
   * The description shown in the popover. Rich content (bold, links, code)
   * is supported so widget-specific vocabulary can be highlighted.
   */
  description: ReactNode;
  /**
   * Short label for screen readers; joined with "info" — e.g. "Cohorts info".
   * The visible glyph is only "i" which is not a meaningful accessible name.
   */
  widgetName: string;
}

interface Layout {
  /**
   * Vertical anchor. When placing below the button, `top` is set to
   * `r.bottom + GAP` (popover extends downward from that y).
   * When placing above, `bottom` is set to `vh - r.top + GAP` — the
   * popover's BOTTOM edge lands GAP above the button's TOP edge, and
   * it extends upward. Using top-only for both cases would put the
   * popover overlapping the button in the place-above case.
   */
  top?: number;
  bottom?: number;
  left: number;
  maxW: number;
  maxH: number;
}

/**
 * Minimum usable popover height, in px. Below this, we prefer placement
 * above (if there's more room there) rather than a cramped placement below.
 */
const MIN_USABLE_H = 120;

const MARGIN = 8;
const GAP = 4;
const MAX_WIDTH_PX = 32 * 16;

export function WidgetInfoButton({
  description,
  widgetName,
}: WidgetInfoButtonProps): JSX.Element {
  const popoverId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState<Layout>({
    top: 0,
    left: 0,
    maxW: MAX_WIDTH_PX,
    maxH: 0,
  });

  const updateLayout = useCallback((): void => {
    const el = buttonRef.current;
    if (el === null) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: clamp width to viewport, then position so the popover
    // stays within viewport bounds (prefer left-anchoring to the button).
    const maxW = Math.min(MAX_WIDTH_PX, vw - 2 * MARGIN);
    let left = r.left;
    if (left + maxW > vw - MARGIN) {
      left = Math.max(MARGIN, vw - MARGIN - maxW);
    }

    // Vertical: prefer below. Flip to above only when below is too cramped
    // AND above has more room. When placing above, anchor via CSS `bottom`
    // (measured from viewport bottom) so the popover extends UPWARD from
    // its bottom edge instead of overlapping the button. `maxH` caps growth
    // strictly to the available space so overflow stays inside .widget-info-popover's
    // internal scroll — no viewport clipping.
    const spaceBelow = vh - r.bottom - GAP - MARGIN;
    const spaceAbove = r.top - GAP - MARGIN;
    const placeAbove = spaceBelow < MIN_USABLE_H && spaceAbove > spaceBelow;

    if (placeAbove) {
      setLayout({
        bottom: vh - r.top + GAP,
        left,
        maxW,
        maxH: Math.max(0, spaceAbove),
      });
    } else {
      setLayout({
        top: r.bottom + GAP,
        left,
        maxW,
        maxH: Math.max(0, spaceBelow),
      });
    }
  }, []);

  // Layout must be computed BEFORE the popover paints; a useEffect would
  // let one frame render at (0,0) before snapping into place.
  useLayoutEffect(() => {
    if (!open) return;
    updateLayout();
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) return;
    const onScroll = (): void => updateLayout();
    const onResize = (): void => updateLayout();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    // pointerdown covers mouse + touch + pen — mousedown alone misses
    // some touch-gesture dismissals on iOS/Android where the synthetic
    // mouse event isn't dispatched (e.g. tap after scroll).
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) === true ||
        popoverRef.current?.contains(target) === true
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="widget-info-button"
        aria-label={`${widgetName} info`}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((v) => !v)}
      >
        {/* aria-hidden on the visible glyph so it doesn't leak into the
         * parent heading's accessible name (h3's a11y name is the
         * concatenation of visible descendant text). The button's
         * aria-label above supplies its accessible name. */}
        <span aria-hidden="true">i</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="dialog"
            aria-label={`${widgetName} info`}
            className="widget-info-popover"
            style={{
              /* Exactly one of top/bottom is set per placement — the other
               * is undefined so React omits it. Position: fixed uses whichever
               * is present as the vertical anchor. */
              top: layout.top,
              bottom: layout.bottom,
              left: layout.left,
              maxWidth: layout.maxW,
              maxHeight: layout.maxH,
            }}
          >
            {description}
          </div>,
          document.body,
        )}
    </>
  );
}
